import React, { useEffect, useMemo, useState } from 'react';

import '../css/profile.css';

const OWNERSHIP_ORDER = ['OWNED', 'ORDERED', 'WANTED'];

function Profile() {
  const [summary, setSummary] = useState(null);
  const [email, setEmail] = useState('');
  const [changeEmail, setChangeEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [sharedWith, setSharedWith] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [dataStatusMessage, setDataStatusMessage] = useState('');
  const [importFile, setImportFile] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteStatusMessage, setInviteStatusMessage] = useState('');
  const [inviteErrorMessage, setInviteErrorMessage] = useState('');

  const user = localStorage.getItem('loggedInUsername') || 'Reader';

  const loadProfileSummary = async (token) => {
    const response = await fetch("/api/library/profile/summary", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({ user }),
    });

    if (!response.ok) {
      throw new Error('Failed to load profile summary');
    }

    return response.json();
  };

  const loadEmail = async (token) => {
    const response = await fetch("/api/auth/email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({ user }),
    });

    if (response.ok) {
      const data = await response.json();
      setEmail(data);
    }
  };

  const loadSharedWith = async (token) => {
    const response = await fetch("/api/auth/sharedwith", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({ user }),
    });

    if (response.ok) {
      const data = await response.json();
      setSharedWith(Array.isArray(data) ? data : []);
    }
  };

  const handleChangeEmail = async () => {
    const token = localStorage.getItem('accessToken');

    if (!newEmail || !token) {
      setStatusMessage('Please enter a new email.');
      return;
    }

    try {
      const response = await fetch("/api/auth/change-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ user, newEmail }),
      });

      if (response.ok) {
        setChangeEmail(false);
        setEmail(newEmail);
        setNewEmail('');
        setStatusMessage('Email updated successfully.');
      } else {
        setStatusMessage('Unable to update email.');
      }
    } catch (error) {
      setStatusMessage('Unable to update email.');
    }
  };

  const handleExportCsv = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setDataStatusMessage('Please sign in to export your library.');
      return;
    }

    try {
      setIsExporting(true);
      setDataStatusMessage('');

      const response = await fetch("/api/library/export-csv", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ user }),
      });

      if (!response.ok) {
        setDataStatusMessage('Unable to export your library right now.');
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `manderley-library-${user}-books.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setDataStatusMessage('Export started. Check your downloads.');
    } catch (error) {
      setDataStatusMessage('Unable to export your library right now.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportCsv = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setDataStatusMessage('Please sign in to import your library.');
      return;
    }
    if (!importFile) {
      setDataStatusMessage('Pick a CSV file to import.');
      return;
    }

    try {
      setIsImporting(true);
      setDataStatusMessage('');

      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('user', user);

      const response = await fetch("/api/library/import-csv", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        setDataStatusMessage(data?.error || 'Unable to import your library.');
        return;
      }

      setDataStatusMessage(
        `Imported ${data.imported} books. ${data.duplicates} duplicates skipped. ${data.skipped} rows skipped.`
      );
    } catch (error) {
      setDataStatusMessage('Unable to import your library.');
    } finally {
      setIsImporting(false);
    }
  };

  const handleCreateInviteLink = async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setInviteErrorMessage('Please sign in to create invite links.');
      setInviteStatusMessage('');
      return;
    }

    try {
      const response = await fetch('/api/auth/create_invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ user })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setInviteErrorMessage(data?.error || 'Unable to create invite link.');
        setInviteStatusMessage('');
        return;
      }

      setInviteLink(data?.invite_link || '');
      setInviteErrorMessage('');
      setInviteStatusMessage('Invite link created. It expires in 12 hours and can be used once.');
    } catch (error) {
      setInviteErrorMessage('Unable to create invite link.');
      setInviteStatusMessage('');
    }
  };

  const handleCopyInviteLink = async () => {
    if (!inviteLink) {
      return;
    }

    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteStatusMessage('Invite link copied to clipboard.');
      setInviteErrorMessage('');
    } catch (error) {
      setInviteErrorMessage('Unable to copy invite link.');
    }
  };

  useEffect(() => {
    const loadProfile = async () => {
      const token = localStorage.getItem('accessToken');

      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setErrorMessage('');

        const summaryData = await loadProfileSummary(token);
        setSummary(summaryData);

        await Promise.all([
          loadEmail(token),
          loadSharedWith(token)
        ]);
      } catch (error) {
        setErrorMessage('Unable to load your profile right now.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, []);

  const totals = summary?.totals || { total_books: 0, read_books: 0, unread_books: 0 };
  const collectionsCount = summary?.collections?.count || 0;
  const activity = summary?.activity || { last_7_days: 0, last_30_days: 0 };
  const ownership = summary?.ownership || { OWNED: 0, ORDERED: 0, WANTED: 0 };
  const formats = summary?.formats || { physical: 0, digital: 0 };
  const recentBooks = summary?.recent_books || [];
  const topAuthors = summary?.top_authors || [];

  const readPercent = useMemo(() => {
    if (!totals.total_books) {
      return 0;
    }
    return Math.round((totals.read_books / totals.total_books) * 100);
  }, [totals]);

  const unreadPercent = useMemo(() => {
    if (!totals.total_books) {
      return 0;
    }
    return Math.round((totals.unread_books / totals.total_books) * 100);
  }, [totals]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) {
      return 'Unknown';
    }
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const getBookImage = (book) => {
    if (book.use_custom_image && book.uploaded_image_b64) {
      return book.uploaded_image_b64;
    }

    if (book.imageLinks && book.imageLinks !== "{}") {
      try {
        const links = JSON.parse(book.imageLinks);
        return links.thumbnail || '';
      } catch (error) {
        return '';
      }
    }

    return '';
  };

  const ownershipItems = OWNERSHIP_ORDER.map((key) => ({
    label: key.charAt(0) + key.slice(1).toLowerCase(),
    value: ownership[key] || 0
  }));

  const formatItems = [
    { label: 'Physical', value: formats.physical || 0 },
    { label: 'Digital', value: formats.digital || 0 }
  ];

  const renderBreakdown = (items, total) => (
    <div className="profile-breakdown">
      {items.map((item) => {
        const percent = total ? Math.round((item.value / total) * 100) : 0;
        return (
          <div className="profile-breakdown-row" key={item.label}>
            <div className="profile-breakdown-label">
              <span>{item.label}</span>
              <span>{item.value}</span>
            </div>
            <div className="profile-meter">
              <div className="profile-meter-fill" style={{ width: `${percent}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className='lib-page profile-page'>
      {isLoading && (
        <div className="profile-loading">Building your dashboard...</div>
      )}

      {!isLoading && errorMessage && (
        <div className="profile-error">{errorMessage}</div>
      )}

      {!isLoading && !errorMessage && (
        <>
          <section className="profile-hero">
            <div className="profile-hero-content">
              <p className="profile-eyebrow">Reader Dashboard</p>
              <h2 className="profile-title">{user}'s Library Pulse</h2>
              <p className="profile-subtitle">
                A quick look at your shelves, recent momentum, and the stories defining your year.
              </p>
              <div className="profile-hero-meta">
                <span className="profile-chip">@{user}</span>
                <span className="profile-chip">{collectionsCount} collections</span>
                <span className="profile-chip">{activity.last_30_days} added in 30 days</span>
              </div>
            </div>
            <div className="profile-progress-card">
              <div className="progress-ring" style={{ "--progress": readPercent }} />
              <div className="progress-text">
                <span className="progress-value">{readPercent}%</span>
                <span className="progress-label">Read</span>
                <span className="progress-sub">{unreadPercent}% left to go</span>
              </div>
            </div>
          </section>

          <section className="profile-grid">
            {[
              { label: 'Total Books', value: totals.total_books },
              { label: 'Read', value: totals.read_books },
              { label: 'Unread', value: totals.unread_books },
              { label: 'Collections', value: collectionsCount },
              { label: 'Added in 7 days', value: activity.last_7_days },
              { label: 'Added in 30 days', value: activity.last_30_days }
            ].map((card, index) => (
              <div className="profile-card" key={card.label} style={{ "--delay": `${index * 0.08}s` }}>
                <p className="profile-card-label">{card.label}</p>
                <p className="profile-card-value">{card.value}</p>
              </div>
            ))}
          </section>

          <section className="profile-split">
            <article className="profile-panel">
              <h3>Ownership mix</h3>
              <p className="profile-panel-sub">How your shelves break down right now.</p>
              {renderBreakdown(ownershipItems, totals.total_books)}
            </article>
            <article className="profile-panel">
              <h3>Format mix</h3>
              <p className="profile-panel-sub">Print versus digital across your library.</p>
              {renderBreakdown(formatItems, totals.total_books)}
            </article>
          </section>

          <section className="profile-split">
            <article className="profile-panel">
              <h3>Recent additions</h3>
              <p className="profile-panel-sub">Fresh arrivals from the past few sessions.</p>
              <div className="profile-recent">
                {recentBooks.length === 0 && (
                  <div className="profile-empty">No recent titles yet.</div>
                )}
                {recentBooks.map((book) => {
                  const image = getBookImage(book);
                  return (
                    <div className="profile-recent-item" key={book.id}>
                      <div className="profile-recent-thumb">
                        {image ? (
                          <img src={image} alt={book.title} />
                        ) : (
                          <span>{book.title.charAt(0)}</span>
                        )}
                      </div>
                      <div className="profile-recent-meta">
                        <p className="profile-recent-title" title={book.title}>{book.title}</p>
                        <p className="profile-recent-author">{book.author || 'Unknown author'}</p>
                        <p className="profile-recent-date">Added {formatTimestamp(book.timestamp)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>
            <article className="profile-panel">
              <h3>Top authors</h3>
              <p className="profile-panel-sub">The writers that appear most on your shelves.</p>
              <div className="profile-list">
                {topAuthors.length === 0 && (
                  <div className="profile-empty">Add a few books to see your top authors.</div>
                )}
                {topAuthors.map((author) => (
                  <div className="profile-list-row" key={author.name}>
                    <span>{author.name}</span>
                    <span className="profile-pill">{author.count} books</span>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section className="profile-split">
            <article className="profile-panel">
              <h3>Sharing network</h3>
              <p className="profile-panel-sub">People who can see your shared shelves.</p>
              <div className="profile-list">
                {sharedWith.length === 0 && (
                  <div className="profile-empty">You are not sharing your library yet.</div>
                )}
                {sharedWith.map((shared) => (
                  <div className="profile-list-row" key={shared.name}>
                    <span>{shared.name}</span>
                    <span className="profile-pill">Shared</span>
                  </div>
                ))}
              </div>
            </article>
            <article className="profile-panel">
              <h3>Account</h3>
              <p className="profile-panel-sub">Keep your account details fresh.</p>
              <div className="profile-account">
                <div className="profile-account-row">
                  <span>Email</span>
                  <span className="profile-pill">{email || 'Not set'}</span>
                </div>
                <button className="profile-action" onClick={() => setChangeEmail(true)}>
                  Change email
                </button>
                {statusMessage && (
                  <div className="profile-status">{statusMessage}</div>
                )}
                {changeEmail && (
                  <div className="profile-form">
                    <label>
                      New email
                      <input
                        type="text"
                        value={newEmail}
                        onChange={(e) => setNewEmail(e.target.value)}
                      />
                    </label>
                    <div className="profile-form-actions">
                      <button className="profile-action" onClick={handleChangeEmail}>Save</button>
                      <button className="profile-secondary" onClick={() => setChangeEmail(false)}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </article>
          </section>

          <section className="profile-split">
            <article className="profile-panel">
              <h3>Library data</h3>
              <p className="profile-panel-sub">Import or export your catalog as CSV.</p>
              <div className="profile-data">
                <div className="profile-data-actions">
                  <button className="profile-action" onClick={handleExportCsv} disabled={isExporting}>
                    {isExporting ? 'Exporting...' : 'Export CSV'}
                  </button>
                </div>
                <div className="profile-file-row">
                  <input
                    className="profile-file-input"
                    id="csv-import"
                    type="file"
                    accept=".csv"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      setImportFile(file || null);
                      setImportFileName(file ? file.name : '');
                    }}
                  />
                  <label className="profile-secondary" htmlFor="csv-import">
                    Choose CSV
                  </label>
                  <span className="profile-file-name">
                    {importFileName || 'No file selected'}
                  </span>
                </div>
                <div className="profile-data-actions">
                  <button className="profile-action" onClick={handleImportCsv} disabled={isImporting || !importFile}>
                    {isImporting ? 'Importing...' : 'Import CSV'}
                  </button>
                </div>
                <p className="profile-helper">Use the export file to preserve collections and states.</p>
                {dataStatusMessage && (
                  <div className="profile-status">{dataStatusMessage}</div>
                )}
              </div>
            </article>
          </section>

          <section className="profile-split">
            <article className="profile-panel">
              <h3>Inviting friends</h3>
              <p className="profile-panel-sub">Create a one-time invite link that expires in 12 hours.</p>
              <div className="profile-data">
                <div className="profile-data-actions">
                  <button className="profile-action" onClick={handleCreateInviteLink}>
                    Create invite link
                  </button>
                </div>
                <div className="profile-form">
                  <label>
                    Invite link
                    <input
                      type="text"
                      value={inviteLink}
                      readOnly
                      placeholder="Create an invite link to share"
                    />
                  </label>
                </div>
                <div className="profile-data-actions">
                  <button className="profile-secondary" onClick={handleCopyInviteLink} disabled={!inviteLink}>
                    Copy invite link
                  </button>
                </div>
                {inviteStatusMessage && (
                  <div className="profile-status">{inviteStatusMessage}</div>
                )}
                {inviteErrorMessage && (
                  <div className="profile-error">{inviteErrorMessage}</div>
                )}
              </div>
            </article>
          </section>
        </>
      )}
    </div>
  );
}

export default Profile;
